import React, { useState, useRef, useEffect } from "react";
import { Modal, Button, Form, FormGroup, FormLabel, FormControl } from "react-bootstrap";
import { FiX, FiPlus } from "../../../icons";
import { toast } from 'react-toastify';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import UploadIcon from '../../../images/profile-dp.svg';
import { contactApi, Contact } from './contactApi.tsx';
import { locationApi, State, City } from '../Auth/utils/LocationApi.tsx';
import CustomSelect from '../../common/CustomSelect/CustomSelect.tsx';
import SearchableSelect from '../../common/SearchableSelect/SearchableSelect.tsx';
import ImageCropModal from '../Profile/ImageCropModal';
// Import the existing phone formatter utility
import {
    getRawPhoneDigits,
    handlePhoneKeyDown,
    createPhoneChangeHandler
} from '../Auth/utils/phoneFormatter.js';

interface AddContactModalProps {
    show: boolean;
    onHide: () => void;
    onAdd: (contact: Contact) => void;
}

// Updated validation schema to use the phone formatter utilities
const validationSchema = Yup.object({
    name: Yup.string()
        .trim()
        .matches(/^[a-zA-Z\s]+$/, 'Only letters and spaces are allowed')
        .required('Name is required'),
    relationship: Yup.string()
        .trim()
        .min(3, 'Must be at least 3 characters')
        .matches(/^[a-zA-Z\s]+$/, 'Only letters and spaces are allowed')
        .required('Relationship is required'),
    address1: Yup.string()
        .trim()
        .required('Address 1 is required'),
    address2: Yup.string().trim(),
    city: Yup.string()
        .required('City is required'),
    state: Yup.string()
        .required('State is required'),
    zip: Yup.string()
        .trim()
        .matches(/^\d{5}(-\d{4})?$/, 'Please enter a valid ZIP code')
        .required('ZIP code is required'),
    email: Yup.string()
        .trim()
        .email('Please enter a valid email address')
        .required('Email is required'),
   phoneNumber: Yup.string()
           .required('Phone number is required')
           .test('phone-validation', 'Phone number must be exactly 10 digits', function(value) {
               if (!value) return false;
               const digits = getRawPhoneDigits(value);
               // Only validate if user has started entering digits (more than just +1)
               if (value.trim() === '+1' || value.trim() === '') {
                   return false; // Show required error instead
               }
               return digits.length === 10;
           }),
    alternatePhoneNumber: Yup.string()
        .test('phone-validation', 'Phone number must be exactly 10 digits', function(value) {
            // Check if value is empty, null, undefined, or just "+1"
            if (!value || value.trim() === '' || value.trim() === '+1') {
                return true; // Allow empty values for optional field
            }
            const digits = getRawPhoneDigits(value);
            return digits.length === 10;
        })
        .nullable()
});

const AddContactModal: React.FC<AddContactModalProps> = ({ show, onHide, onAdd }) => {
    const [image, setImage] = useState<string | null>(null);
    const [imageFile, setImageFile] = useState<File | null>(null);
    const [states, setStates] = useState<State[]>([]);
    const [cities, setCities] = useState<City[]>([]);
    const [loading, setLoading] = useState(false);
    const [loadingCities, setLoadingCities] = useState(false);
    const [showCropModal, setShowCropModal] = useState<boolean>(false);
    const [selectedImageSrc, setSelectedImageSrc] = useState<string>('');
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Load states on component mount
    useEffect(() => {
        loadStates();
    }, []);

    const loadStates = async () => {
        try {
            const response = await locationApi.getAllStates();
            if (response.status === 'Success' && response.states) {
                setStates(response.states);
            }
        } catch (error) {
            console.error('Error loading states:', error);
            toast.error('Failed to load states');
        }
    };

    // Updated function to load cities by state ID (which uses stateCode internally)
    const loadCitiesByStateId = async (stateId: string) => {
        if (!stateId) {
            setCities([]);
            return;
        }

        try {
            setLoadingCities(true);
            
            // Use getCitiesByStateId which will use stateCode if available
            const response = await locationApi.getCitiesByStateId(stateId);
            
            if (response.status === 'Success' && response.cities) {
                setCities(response.cities);
            } else {
                setCities([]);
                toast.error('Failed to load cities for selected state');
            }
        } catch (error) {
            console.error('Error loading cities:', error);
            setCities([]);
            toast.error('Failed to load cities');
        } finally {
            setLoadingCities(false);
        }
    };

    const formik = useFormik({
        initialValues: {
            name: '',
            relationship: '',
            address1: '',
            address2: '',
            city: '',
            state: '',
            zip: '',
            email: '',
            phoneNumber: '+1',
            alternatePhoneNumber: '+1'
        },
        validationSchema,
        onSubmit: async (values, { setSubmitting, resetForm }) => {
            try {
                setLoading(true);
                
                const contactData: Omit<Contact, '_id'> = {
                    name: values.name.trim(),
                    relationship: values.relationship.trim(),
                    address1: values.address1.trim(),
                    address2: values.address2?.trim() || '',
                    city: values.city,
                    state: values.state,
                    zip: values.zip.trim(),
                    email: values.email.trim().toLowerCase(),
                    // Convert formatted phone numbers to raw digits for API
                    phoneNumber: getRawPhoneDigits(values.phoneNumber),
                    alternatePhoneNumber: getRawPhoneDigits(values.alternatePhoneNumber) || '',
                    oldImage: ''
                };

                const response = await contactApi.addContact(contactData, imageFile || undefined);
                
                if (response.status === 'Success' && response.contact) {
                    toast.success(response.message || 'Contact added successfully!');
                    onAdd(response.contact);
                    resetForm();
                    setImage(null);
                    setImageFile(null);
                    setCities([]); // Reset cities when form is reset
                    onHide();
                } else {
                    toast.error(response.message || 'Failed to add contact');
                }
            } catch (error: any) {
                toast.error(error.message || 'Failed to add contact');
            } finally {
                setLoading(false);
                setSubmitting(false);
            }
        }
    });

    // Updated handleStateChange to use state ID and load cities by stateCode
    const handleStateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const selectedStateId = e.target.value;

        // Find the selected state to get its name for storing in the form
        const selectedState = states.find(state => state._id === selectedStateId);

        if (selectedState) {
            formik.setFieldValue('state', selectedState.name); // Store state name in form
            formik.setFieldValue('city', ''); // Reset city when state changes
            formik.setFieldValue('zip', ''); // Clear zip code when state changes

            // Load cities using state ID (which will use stateCode internally)
            loadCitiesByStateId(selectedStateId);
        } else {
            formik.setFieldValue('state', '');
            formik.setFieldValue('city', '');
            formik.setFieldValue('zip', '');
            setCities([]);
        }
    };

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const file = e.target.files?.[0];
        if (file) {
            // Validate file type
            if (!file.type.startsWith('image/')) {
                toast.error('Please select a valid image file');
                return;
            }

            // Validate file size (max 5MB)
            if (file.size > 5 * 1024 * 1024) {
                toast.error('Image size must be less than 5MB');
                return;
            }

            // Create a preview URL for the cropper
            const reader = new FileReader();
            reader.onloadend = () => {
                setSelectedImageSrc(reader.result as string);
                setShowCropModal(true);
            };
            reader.readAsDataURL(file);

            // Reset file input so the same file can be selected again
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const handleCropComplete = (croppedImageBlob: Blob): void => {
        const imageUrl = URL.createObjectURL(croppedImageBlob);
        setImage(imageUrl);
        setImageFile(new File([croppedImageBlob], "contact-photo.jpg", { type: 'image/jpeg' }));
    };

    const handleClearImage = (): void => {
        setImage(null);
        setImageFile(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    // Use the phone formatter utility to create phone change handlers
    const handlePhoneNumberChange = createPhoneChangeHandler(formik, 'phoneNumber');
    const handleAlternatePhoneChange = createPhoneChangeHandler(formik, 'alternatePhoneNumber');

    const handleNameChange = (fieldName: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.replace(/[^a-zA-Z\s]/g, '');
        formik.setFieldValue(fieldName, value);
    };

    // Enhanced address change handler with explicit space handling
    const handleAddressChange = (field: 'address1' | 'address2') => (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        // Explicitly allow all characters including spaces, numbers, and special characters for addresses
        console.log(`Address ${field} change:`, value, 'Has spaces:', value.includes(' '));
        formik.setFieldValue(field, value);
    };

    // Handle key events to ensure spaces are not blocked
    const handleAddressKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        // Ensure space key (keyCode 32) is not prevented
        if (e.key === ' ' || e.keyCode === 32) {
            e.stopPropagation();
        }
    };

    const handleClose = () => {
        formik.resetForm();
        setImage(null);
        setImageFile(null);
        setCities([]); // Reset cities when modal closes
        onHide();
    };

    return (
        <Modal show={show} onHide={handleClose} centered size="lg" className='main-modal'>
            <Modal.Header closeButton>
                <Modal.Title>Add Contact</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form onSubmit={formik.handleSubmit}>
                    <div className='row'>
                        <div className="profile-update">
                            <div className="profile-update-in">
                                <div
                                    style={{
                                        position: 'relative',
                                        width: 120,
                                        height: 120,
                                        margin: '10px auto 30px',
                                    }}
                                >
                                    <img
                                        src={image || UploadIcon}
                                        alt="Profile"
                                        style={{
                                            width: 120,
                                            height: 120,
                                            borderRadius: '50%',
                                            objectFit: 'cover',
                                            border: '2px solid #d0d0d0',
                                        }}
                                    />

                                    <input
                                        type="file"
                                        accept="image/*"
                                        ref={fileInputRef}
                                        onChange={handleImageChange}
                                        style={{ display: 'none' }}
                                    />

                                    <button
                                        type="button"
                                        className="plus-bttn"
                                        onClick={() => fileInputRef.current?.click()}
                                        style={{
                                            position: 'absolute',
                                            bottom: 0,
                                            right: 0,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            cursor: 'pointer',
                                        }}
                                        title="Upload Photo"
                                        disabled={loading}
                                    >
                                        <FiPlus />
                                    </button>

                                    {image && (
                                        <button
                                            type="button"
                                            className="clear-bttn"
                                            onClick={handleClearImage}
                                            style={{
                                                position: 'absolute',
                                                top: 0,
                                                right: 0,
                                                backgroundColor: '#fff',
                                                borderRadius: '50%',
                                                border: '1px solid #ccc',
                                                width: 24,
                                                height: 24,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                cursor: 'pointer',
                                            }}
                                            title="Remove Image"
                                        >
                                            <FiX />
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>

                        <FormGroup className='form-group col-md-6'>
                            <FormLabel>Name *</FormLabel>
                            <FormControl 
                                type="text" 
                                placeholder="Enter Name"
                                name="name"
                                value={formik.values.name}
                                onChange={handleNameChange('name')}
                                onBlur={formik.handleBlur}
                                disabled={loading}
                            />
                            {formik.touched.name && formik.errors.name && (
                                <small style={{
                                    color: '#dc3545',
                                    display: 'block',
                                    marginTop: '4px',
                                    fontSize: '0.875rem'
                                }}>
                                    {formik.errors.name}
                                </small>
                            )}
                        </FormGroup>

                        <FormGroup className='form-group col-md-6'>
                            <FormLabel>Relationship *</FormLabel>
                            <FormControl 
                                type="text" 
                                placeholder="Enter Relationship"
                                name="relationship"
                                value={formik.values.relationship}
                                onChange={handleNameChange('relationship')}
                                onBlur={formik.handleBlur}
                                disabled={loading}
                            />
                            {formik.touched.relationship && formik.errors.relationship && (
                                <small style={{
                                    color: '#dc3545',
                                    display: 'block',
                                    marginTop: '4px',
                                    fontSize: '0.875rem'
                                }}>
                                    {formik.errors.relationship}
                                </small>
                            )}
                        </FormGroup>

                        <FormGroup className='form-group col-md-6'>
                            <FormLabel>Address 1 *</FormLabel>
                            <FormControl 
                                type="text" 
                                placeholder="Enter Address 1 (e.g., 123 Main Street)"
                                name="address1"
                                value={formik.values.address1}
                                onChange={handleAddressChange('address1')}
                                onKeyDown={handleAddressKeyDown}
                                onBlur={formik.handleBlur}
                                disabled={loading}
                                autoComplete="address-line1"
                                spellCheck={false}
                            />
                            {formik.touched.address1 && formik.errors.address1 && (
                                <small style={{
                                    color: '#dc3545',
                                    display: 'block',
                                    marginTop: '4px',
                                    fontSize: '0.875rem'
                                }}>
                                    {formik.errors.address1}
                                </small>
                            )}
                        </FormGroup>

                        <FormGroup className='form-group col-md-6'>
                            <FormLabel>Address 2</FormLabel>
                            <FormControl 
                                type="text" 
                                placeholder="Enter Address 2 (e.g., Apt 4B, Suite 100)"
                                name="address2"
                                value={formik.values.address2}
                                onChange={handleAddressChange('address2')}
                                onKeyDown={handleAddressKeyDown}
                                disabled={loading}
                                autoComplete="address-line2"
                                spellCheck={false}
                            />
                        </FormGroup>

                        {/* State Dropdown with Custom Select */}
                        <FormGroup className='form-group col-md-4'>
                            <FormLabel>State *</FormLabel>
                            <SearchableSelect
                                options={states.map(state => ({
                                    value: state._id,
                                    label: state.name
                                }))}
                                value={states.find(state => state.name === formik.values.state)?._id || ''}
                                placeholder="Select State"
                                onChange={(value) => {
                                    const selectedState = states.find(state => state._id === value);
                                    if (selectedState) {
                                        formik.setFieldValue('state', selectedState.name);
                                        handleStateChange({ target: { value } } as any);
                                    }
                                }}
                                disabled={loading}
                                loading={loading}
                                loadingText="Loading states..."
                                isInvalid={!!(formik.touched.state && formik.errors.state)}
                                searchPlaceholder="Search states..."
                                noOptionsText="No states found"
                            />
                            {formik.touched.state && formik.errors.state && (
                                <small style={{
                                    color: '#dc3545',
                                    display: 'block',
                                    marginTop: '4px',
                                    fontSize: '0.875rem'
                                }}>
                                    {formik.errors.state}
                                </small>
                            )}
                        </FormGroup>

                        {/* City Dropdown with Custom Select */}
                        <FormGroup className='form-group col-md-5'>
                            <FormLabel>City *</FormLabel>
                            <SearchableSelect
                                options={cities.map(city => ({
                                    value: city.name,
                                    label: city.name
                                }))}
                                value={formik.values.city}
                                placeholder={
                                    !formik.values.state 
                                        ? 'Select State First' 
                                        : 'Select City'
                                }
                                onChange={(value) => {
                                    formik.setFieldValue('city', value);
                                }}
                                disabled={loading || loadingCities || !formik.values.state}
                                loading={loadingCities}
                                loadingText="Loading cities..."
                                isInvalid={!!(formik.touched.city && formik.errors.city)}
                                searchPlaceholder="Search cities..."
                                noOptionsText="No cities found"
                            />
                            {formik.touched.city && formik.errors.city && (
                                <small style={{
                                    color: '#dc3545',
                                    display: 'block',
                                    marginTop: '4px',
                                    fontSize: '0.875rem'
                                }}>
                                    {formik.errors.city}
                                </small>
                            )}
                        </FormGroup>

                        <FormGroup className='form-group col-md-3'>
                            <FormLabel>Zip *</FormLabel>
                            <FormControl 
                                type="text" 
                                placeholder="Enter Zip"
                                name="zip"
                                value={formik.values.zip}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                disabled={loading}
                                maxLength={5}
                            />
                            {formik.touched.zip && formik.errors.zip && (
                                <small style={{
                                    color: '#dc3545',
                                    display: 'block',
                                    marginTop: '4px',
                                    fontSize: '0.875rem'
                                }}>
                                    {formik.errors.zip}
                                </small>
                            )}
                        </FormGroup>

                        <FormGroup className='form-group col-md-6'>
                            <FormLabel>Email Address *</FormLabel>
                            <FormControl 
                                type="email" 
                                placeholder="Enter Email Address"
                                name="email"
                                value={formik.values.email}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                disabled={loading}
                            />
                            {formik.touched.email && formik.errors.email && (
                                <small style={{
                                    color: '#dc3545',
                                    display: 'block',
                                    marginTop: '4px',
                                    fontSize: '0.875rem'
                                }}>
                                    {formik.errors.email}
                                </small>
                            )}
                        </FormGroup>

                        {/* Enhanced Phone Number field with formatting */}
                        <FormGroup className='form-group col-md-6'>
                            <FormLabel>Phone Number *</FormLabel>
                            <FormControl 
                                type="tel" 
                                placeholder="Enter Phone Number"
                                name="phoneNumber"
                                value={formik.values.phoneNumber}
                                onChange={handlePhoneNumberChange}
                                onKeyDown={handlePhoneKeyDown}
                                onBlur={formik.handleBlur}
                                disabled={loading}
                            />
                            {formik.touched.phoneNumber && formik.errors.phoneNumber && (
                                <small style={{
                                    color: '#dc3545',
                                    display: 'block',
                                    marginTop: '4px',
                                    fontSize: '0.875rem'
                                }}>
                                    {formik.errors.phoneNumber}
                                </small>
                            )}
                            
                        </FormGroup>

                        {/* Enhanced Alternate Phone Number field with formatting */}
                        <FormGroup className='form-group col-md-6'>
                            <FormLabel>Alternate Phone Number (Optional)</FormLabel>
                            <FormControl 
                                type="tel" 
                                placeholder="Enter Alternate Phone Number"
                                name="alternatePhoneNumber"
                                value={formik.values.alternatePhoneNumber}
                                onChange={handleAlternatePhoneChange}
                                onKeyDown={handlePhoneKeyDown}
                                onBlur={formik.handleBlur}
                                disabled={loading}
                            />
                            {formik.touched.alternatePhoneNumber && formik.errors.alternatePhoneNumber && (
                                <small style={{
                                    color: '#dc3545',
                                    display: 'block',
                                    marginTop: '4px',
                                    fontSize: '0.875rem'
                                }}>
                                    {formik.errors.alternatePhoneNumber}
                                </small>
                            )}
                            
                        </FormGroup>

                        <div className='text-center mt-4'>
                            <Button 
                                className="main-bttn" 
                                type="submit"
                                disabled={loading || formik.isSubmitting}
                            >
                                {loading ? 'Adding Contact...' : 'Add Contact'}
                            </Button>
                        </div>
                    </div>
                </Form>
            </Modal.Body>

            {/* Image Crop Modal */}
            <ImageCropModal
                show={showCropModal}
                imageSrc={selectedImageSrc}
                onHide={() => setShowCropModal(false)}
                onCropComplete={handleCropComplete}
            />
        </Modal>
    );
};

export default AddContactModal;